import { useMemo } from 'react';
import { FaFileDownload } from 'react-icons/fa';
import { HeaderGroup } from 'react-table';
import { Button } from '@nbirl/nbi-common-ui-libs';

import {
  useCurrentUser,
  useDownload,
} from '../../../../../../../shared/context';
import { AppFeature, Permission } from '../../../../../../../shared/enum';
import { hasFeatureEnabled } from '../../../../../../../shared/utils';
import { DocumentListRow } from '../../../../models';
import { BulkChangeStatus } from './bulk-change-status.component';

type BulkActionsHeaderProps = {
  headerGroups: HeaderGroup<object>[];
  selectedRows: DocumentListRow[];
  onDeselectRows: () => void;
  onBulkStatusChange: () => void;
};

export const BulkActionsHeader = ({
  headerGroups,
  selectedRows,
  onDeselectRows,
  onBulkStatusChange,
}: BulkActionsHeaderProps) => {
  const { permissions } = useCurrentUser();
  const { isDownloading, download } = useDownload();

  const folders = selectedRows.filter((row) => row.isFolder);
  const documents = selectedRows.filter((row) => !row.isFolder);
  const { headers } = headerGroups[0];
  const selectionColumn = headers[0].id === 'selection' ? headers[0] : null;
  const actionHeaderColSpan = selectionColumn
    ? Math.max(headers.length - 1, 0)
    : headers.length;

  const handleDownload = () => {
    const folderIds = folders.map((row) => row.id);
    const documentIds = documents.map((row) => row.id);

    download({ folderIds, documentIds });
    onDeselectRows();
  };

  const handleStatusChangeSuccess = () => {
    onDeselectRows();
    onBulkStatusChange();
  };

  const canBulkChangeStatus = useMemo<boolean>(
    () =>
      permissions.includes(Permission.ChangeDocumentStatus) &&
      hasFeatureEnabled(AppFeature.BulkChangeDocumentStatus),
    [permissions]
  );

  return (
    <tr data-testid="bulk-actions-row">
      {selectionColumn && (
        <th className="px-2 py-2 w-0 text-left border-b border-gray-100">
          {selectionColumn.render('Header')}
        </th>
      )}
      <th
        colSpan={actionHeaderColSpan}
        className="text-left border-b border-gray-100"
      >
        <Button
          variant="ghost"
          size="md"
          leftIcon={<FaFileDownload />}
          disabled={isDownloading}
          onClick={handleDownload}
        >
          Download
        </Button>

        {canBulkChangeStatus && (
          <BulkChangeStatus
            folders={folders}
            documents={documents}
            onStatusChangeSuccess={handleStatusChangeSuccess}
          />
        )}
      </th>
    </tr>
  );
};
